// Trial balance (contract §9: default export; `sumZero === false` renders an alarm state
// carrying `role="alert"` AND a text label — money-correctness signals are never colour
// alone). GET /api/super-admin/gateway/ledger/trial-balance.
import React, { useCallback, useEffect, useState } from "react";
import { useLanguage, useT } from "../../lib/LanguageContext";
import { formatMoneyLedger } from "../../lib/formatMoney";
import { LEDGER_ACCOUNTS, ACCOUNT_SIGN } from "../contract";
import defaultGatewayApi from "../api";
import { Button } from "../../components/ui/button";
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, Scale } from "lucide-react";

// ONE LABEL PER MEMBER OF `LEDGER_ACCOUNTS`. The table below renders a row for every
// account in that closed set and falls back to "gw.console.account.unknown" for one it
// does not name — a silent fallback, since a missing entry renders a real sentence
// ("Unknown account") rather than a raw key, which is how `giftcard_liability` sat
// unlabelled in the operator's trial balance from the day it was added until 2026-09-16.
const ACCOUNT_KEYS = {
  platform_cash: "gw.console.account.platform_cash",
  tenant_payable: "gw.console.account.tenant_payable",
  platform_revenue_commission: "gw.console.account.platform_revenue_commission",
  platform_revenue_subscription: "gw.console.account.platform_revenue_subscription",
  stripe_fees: "gw.console.account.stripe_fees",
  refunds_payable: "gw.console.account.refunds_payable",
  dispute_losses: "gw.console.account.dispute_losses",
  tenant_adjustments: "gw.console.account.tenant_adjustments",
  platform_adjustment_expense: "gw.console.account.platform_adjustment_expense",
  platform_equity_opening: "gw.console.account.platform_equity_opening",
  giftcard_liability: "gw.console.account.giftcard_liability",
  affiliate_payable: "gw.console.account.affiliate_payable",
  platform_affiliate_expense: "gw.console.account.platform_affiliate_expense",
};

/** @param {object} [gatewayApi] DI seam (contract §9) */
export default function TrialBalancePanel({ gatewayApi = defaultGatewayApi }) {
  const t = useT("superadmin");
  const { lang } = useLanguage();
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await gatewayApi.adminTrialBalance();
      setBalance(data);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [gatewayApi]);

  useEffect(() => {
    reload();
  }, [reload]);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4" data-testid="trial-balance-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2">
          <Scale className="w-4 h-4 text-slate-400" /> {t("gw.console.trialBalance.title")}
        </h3>
        <Button variant="outline" size="sm" onClick={reload} disabled={loading} data-testid="trial-balance-refresh">
          {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
          {t("gw.console.trialBalance.refresh")}
        </Button>
      </div>

      {loading && !balance && (
        <p className="text-sm text-slate-400" data-testid="trial-balance-loading">{t("gw.console.loading")}</p>
      )}

      {error && (
        <p className="text-sm text-rose-600" role="alert" data-testid="trial-balance-error">
          {t("gw.console.loadError")}
        </p>
      )}

      {balance && (
        <>
          {/* Alarm on `sumZero`/`driftCents` ALONE — contract §4: `accounts` are
              sign-corrected natural balances that must never be summed by a consumer;
              only the backend's own `driftCents` (Σ balance × ACCOUNT_SIGN) is the
              double-entry check. */}
          {balance.sumZero === false ? (
            <div
              role="alert"
              className="rounded-xl border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-800 flex items-center gap-2"
              data-testid="trial-balance-alarm"
            >
              <AlertTriangle className="w-5 h-5 flex-shrink-0" />
              <span className="font-semibold">
                {t("gw.console.trialBalance.unbalanced", { amount: formatMoneyLedger(balance.driftCents, balance.currency, lang) })}
              </span>
            </div>
          ) : (
            <div
              className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 flex items-center gap-2"
              data-testid="trial-balance-ok"
            >
              <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
              <span className="font-semibold">{t("gw.console.trialBalance.balanced")}</span>
            </div>
          )}

          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-100">
                <th className="py-2 pr-4">{t("gw.console.trialBalance.colAccount")}</th>
                <th className="py-2 text-right">{t("gw.console.trialBalance.colBalance")}</th>
              </tr>
            </thead>
            <tbody>
              {LEDGER_ACCOUNTS.map((acct) => (
                <tr key={acct} className="border-b border-slate-50">
                  <td className="py-2 pr-4 text-slate-700">
                    {t(ACCOUNT_KEYS[acct] || "gw.console.account.unknown")}
                    <span className="ml-1.5 text-[10px] uppercase text-slate-400" title={t("gw.console.trialBalance.normalBalanceHint")}>
                      {ACCOUNT_SIGN[acct] === 1 ? t("gw.console.trialBalance.debitNormal") : t("gw.console.trialBalance.creditNormal")}
                    </span>
                  </td>
                  <td className="py-2 text-right tabular-nums font-medium">
                    {formatMoneyLedger(balance.accounts?.[acct] ?? 0, balance.currency, lang)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
